// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential.

import React, { useState, useRef, useEffect, useCallback } from "react";
import { sGetRaw, sSetRaw, sRemove } from "../lib/scopedStorage";
import * as Icon from "./OEIcons";
import { SOW_TEMPLATES, STANDARD_MISC_ITEMS } from "../lib/sowTemplates";
import { generateSowDocx, generateBidFormDocx } from "../lib/generateDocx";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

interface Message {
  role: "user" | "assistant";
  content: string;
  error?: boolean;
}

interface SchedulePhaseData {
  id: string;
  name: string;
  linkedDivisions: string[];
  linkedDivisionTimings?: Array<{ id: string; startWeek: number; durationWeeks: number }>;
  startWeek: number;
  durationWeeks: number;
}

interface Props {
  projectName: string;
  address: string;
  divisionId: string;
  divisionName: string;
  tradeName: string;
  tradeContact: string;
  estimateLineItems: string[];
  alternates: { description: string; value: string }[];
  proposals: { id: string; name: string }[];
  proposalTotals: Record<string, number>;
  scheduleStart: string;
  scheduleEnd: string;
  scheduleData?: { projectStartDate: string; phases: SchedulePhaseData[] };
  submittalsForDivision?: string[];
  allowancesForDivision?: string[];
  projectContextDoc: string;
  T: any;
  FONT?: string;
  onClose: () => void;
}

interface EditorState {
  scopeItems: string[];
  miscItems: string[];
  documentList: string;
  submittals: string[];
  exclusions: string[];
  referenceSpecs: string[];
  schedule: { start: string; end: string; notes: string };
  allowances: string[];
  alternates: { description: string; value: string }[];
  unitPrices: string[];
  laborRates: string[];
  recapitulation: { description: string; value: string }[];
  mobilizations: number;
  testingInspection: string[];
}

const SECTION_LABELS = [
  "Scope of Work",
  "Misc. Requirements",
  "Document List",
  "Submittals",
  "Exclusions",
  "Reference Standards",
  "Schedule",
  "Allowances",
  "Alternates",
  "Unit Prices",
  "Labor Rates",
  "Contract Recapitulation",
];

function initEditorState(
  divisionId: string,
  estimateLineItems: string[],
  alternates: { description: string; value: string }[],
  proposals: { id: string; name: string }[],
  proposalTotals: Record<string, number>,
  scheduleStart: string,
  scheduleEnd: string,
  liveSubmittals?: string[],
  liveAllowances?: string[]
): EditorState {
  const tmpl = SOW_TEMPLATES[divisionId];

  // Merge template scope with estimate line items (dedup by simple includes check)
  const baseScopeItems = tmpl?.scopeItems || [];
  const extraItems = (estimateLineItems || []).filter(
    li => !baseScopeItems.some(bi => bi.toLowerCase().includes(li.toLowerCase().slice(0, 30)))
  );
  const scopeItems = [...baseScopeItems, ...extraItems];

  // Labor rates: "Category: $___/hr ST | $___/hr OT"
  const laborRates = (tmpl?.laborCategories || []).map(
    cat => `${cat}: $___/hr ST  |  $___/hr OT`
  );

  // Recapitulation from proposals
  const recapRows = proposals.map(p => ({
    description: p.name,
    value: proposalTotals[p.id] ? `$${Math.round(proposalTotals[p.id]).toLocaleString()}` : "$ ___________",
  }));

  // Use live submittals from the Submittals tab when available; fall back to template stubs
  const submittals = (liveSubmittals && liveSubmittals.length > 0)
    ? liveSubmittals
    : (tmpl?.submittals || []);

  return {
    scopeItems,
    miscItems: [...STANDARD_MISC_ITEMS],
    documentList: "Contract documents issued for construction, including all architectural, structural, MEP, and civil drawings and specifications current at time of bid.",
    submittals,
    exclusions: tmpl?.exclusions || [],
    referenceSpecs: tmpl?.referenceSpecs || [],
    schedule: {
      start: scheduleStart || "",
      end: scheduleEnd || "",
      notes: "",
    },
    allowances: (liveAllowances && liveAllowances.length > 0) ? liveAllowances : [],
    alternates: alternates || [],
    unitPrices: [],
    laborRates,
    recapitulation: recapRows,
    mobilizations: 1,
    testingInspection: tmpl?.testingInspection || [],
  };
}

// ─── Derive schedule dates from project schedule phases ────
function deriveScheduleForDivision(
  divisionId: string,
  scheduleData: { projectStartDate: string; phases: SchedulePhaseData[] }
): { start: string; end: string; phaseNames: string[] } | null {
  const { projectStartDate, phases } = scheduleData;
  if (!projectStartDate || !phases?.length) return null;

  const baseDate = new Date(projectStartDate + "T00:00:00");
  let minWeek = Infinity, maxWeek = -Infinity;
  const phaseNames: string[] = [];

  for (const ph of phases) {
    if (!ph.linkedDivisions.includes(divisionId)) continue;
    phaseNames.push(ph.name);
    const timing = ph.linkedDivisionTimings?.find(t => t.id === divisionId);
    const sw = timing ? timing.startWeek : ph.startWeek;
    const ew = timing ? sw + timing.durationWeeks : ph.startWeek + ph.durationWeeks;
    if (sw < minWeek) minWeek = sw;
    if (ew > maxWeek) maxWeek = ew;
  }

  if (!phaseNames.length || minWeek === Infinity) return null;

  const addWeeks = (d: Date, w: number) => {
    const r = new Date(d);
    r.setDate(r.getDate() + Math.round(w * 7));
    return r;
  };
  const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
  const fmtDisplay = (iso: string) => {
    const [y, m, day] = iso.split("-");
    return `${parseInt(m)}/${parseInt(day)}/${y}`;
  };

  const startIso = fmtDate(addWeeks(baseDate, minWeek));
  const endIso   = fmtDate(addWeeks(baseDate, maxWeek));
  return { start: startIso, end: endIso, phaseNames };
}

// ─── Reusable list editor ────
function ListEditor({
  items,
  onChange,
  placeholder = "Add item...",
  T,
}: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  T: any;
}) {
  const updateItem = (idx: number, val: string) => {
    const next = [...items];
    next[idx] = val;
    onChange(next);
  };
  const removeItem = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const addItem = () => onChange([...items, ""]);

  return (
    <div style={{ display: "flex", flexDirection: "column" as any, gap: 6 }}>
      {items.map((item, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
          <span style={{ color: T.textMuted, fontSize: 11, paddingTop: 7, minWidth: 20, textAlign: "right" as any, flexShrink: 0 }}>
            {i + 1}.
          </span>
          <textarea
            value={item}
            onChange={e => updateItem(i, e.target.value)}
            rows={Math.max(2, Math.ceil(item.length / 80))}
            style={{
              flex: 1,
              background: T.bgInput,
              border: `1px solid ${T.borderSecondary}`,
              borderRadius: 6,
              color: T.textPrimary,
              fontFamily: FONT,
              fontSize: 12,
              padding: "6px 10px",
              resize: "vertical" as any,
              lineHeight: 1.5,
              outline: "none",
            }}
            placeholder={placeholder}
          />
          <button
            onClick={() => removeItem(i)}
            style={{ background: "none", border: "none", cursor: "pointer", color: T.textWeak, padding: "6px 4px", flexShrink: 0 }}
            title="Remove"
          >
            <Icon.Trash size={13} />
          </button>
        </div>
      ))}
      <button
        onClick={addItem}
        style={{
          alignSelf: "flex-start",
          background: "none",
          border: `1px dashed ${T.borderSecondary}`,
          borderRadius: 6,
          color: T.textMuted,
          cursor: "pointer",
          fontFamily: FONT,
          fontSize: 11,
          padding: "5px 12px",
          display: "flex",
          alignItems: "center",
          gap: 5,
          marginTop: 2,
        }}
      >
        <Icon.Plus size={11} /> Add item
      </button>
    </div>
  );
}

// ─── Two-column list editor (description + value) ────
function PairListEditor({
  items,
  onChange,
  labelA = "Description",
  labelB = "Value",
  T,
}: {
  items: { description: string; value: string }[];
  onChange: (items: { description: string; value: string }[]) => void;
  labelA?: string;
  labelB?: string;
  T: any;
}) {
  const update = (idx: number, key: "description" | "value", val: string) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [key]: val } : it));
    onChange(next);
  };
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, { description: "", value: "" }]);

  return (
    <div style={{ display: "flex", flexDirection: "column" as any, gap: 6 }}>
      {items.length > 0 && (
        <div style={{ display: "flex", gap: 6, paddingLeft: 20 }}>
          <span style={{ flex: 3, fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: ".07em", textTransform: "uppercase" as any }}>{labelA}</span>
          <span style={{ flex: 1, fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: ".07em", textTransform: "uppercase" as any }}>{labelB}</span>
          <span style={{ width: 28 }} />
        </div>
      )}
      {items.map((item, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ color: T.textMuted, fontSize: 11, minWidth: 20, textAlign: "right" as any, flexShrink: 0 }}>{i + 1}.</span>
          <input
            value={item.description}
            onChange={e => update(i, "description", e.target.value)}
            placeholder={labelA}
            style={{ flex: 3, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "6px 10px", outline: "none" }}
          />
          <input
            value={item.value}
            onChange={e => update(i, "value", e.target.value)}
            placeholder={labelB}
            style={{ flex: 1, background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "6px 10px", outline: "none" }}
          />
          <button onClick={() => remove(i)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textWeak, padding: "4px" }}>
            <Icon.Trash size={13} />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        style={{ alignSelf: "flex-start", background: "none", border: `1px dashed ${T.borderSecondary}`, borderRadius: 6, color: T.textMuted, cursor: "pointer", fontFamily: FONT, fontSize: 11, padding: "5px 12px", display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}
      >
        <Icon.Plus size={11} /> Add row
      </button>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────

const SOW_DRAFT_KEY = (divId: string) => `oe_sow_draft_${divId}`;

export default function SowEditorModal({
  projectName, address, divisionId, divisionName, tradeName, tradeContact,
  estimateLineItems, alternates, proposals, proposalTotals,
  scheduleStart, scheduleEnd, scheduleData, submittalsForDivision, allowancesForDivision, projectContextDoc, T, onClose,
}: Props) {
  const [activeSection, setActiveSection] = useState(0);
  const [state, setState] = useState<EditorState>(() => {
    // Restore saved draft if available
    try {
      const saved = sGetRaw(SOW_DRAFT_KEY(divisionId));
      if (saved) return JSON.parse(saved) as EditorState;
    } catch {}
    return initEditorState(divisionId, estimateLineItems, alternates, proposals, proposalTotals, scheduleStart, scheduleEnd, submittalsForDivision, allowancesForDivision);
  });
  const [showAI, setShowAI] = useState(false);
  const [aiMessages, setAiMessages] = useState<Message[]>([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiConvHistory, setAiConvHistory] = useState<Array<{ role: string; content: string }>>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"saved" | "saving" | null>(null);
  const aiEndRef = useRef<HTMLDivElement>(null);
  const aiInputRef = useRef<HTMLTextAreaElement>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Auto-save draft on every state change (debounced 700ms)
  useEffect(() => {
    setSaveStatus("saving");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try { sSetRaw(SOW_DRAFT_KEY(divisionId), JSON.stringify(state)); } catch {}
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus(null), 2000);
    }, 700);
    return () => clearTimeout(saveTimer.current);
  }, [state]);

  useEffect(() => {
    if (showAI && aiMessages.length === 0) {
      setAiMessages([{
        role: "assistant",
        content: `I'm ready to help you refine the scope of work for **${divisionName}** on **${projectName}**. Based on the project context and your estimate, I can suggest improvements to make this scope more comprehensive and project-specific.\n\nWhat would you like to improve? You can ask about a specific section, request additional scope items, or ask me to check for gaps.`,
      }]);
    }
  }, [showAI]);

  useEffect(() => {
    aiEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [aiMessages, aiLoading]);

  const showToastMsg = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleResetToDefaults = () => {
    if (!confirm(`Reset all sections to template defaults for ${divisionName}? Your edits will be lost.`)) return;
    const fresh = initEditorState(divisionId, estimateLineItems, alternates, proposals, proposalTotals, scheduleStart, scheduleEnd, submittalsForDivision, allowancesForDivision);
    setState(fresh);
    try { sRemove(SOW_DRAFT_KEY(divisionId)); } catch {}
    showToastMsg("Reset to defaults");
  };

  const upd = useCallback(<K extends keyof EditorState>(key: K, val: EditorState[K]) => {
    setState(prev => ({ ...prev, [key]: val }));
  }, []);

  const handleAiSend = async () => {
    const q = aiInput.trim();
    if (!q || aiLoading) return;
    setAiInput("");
    setAiMessages(prev => [...prev, { role: "user", content: q }]);
    setAiLoading(true);

    try {
      const { supabase } = await import("../lib/supabase");
      if (!supabase) throw new Error("Not connected — please sign in.");

      const contextWithSow = `${projectContextDoc}\n\nCurrent section being reviewed: ${SECTION_LABELS[activeSection]}\nDivision: ${divisionName}\nTrade Contractor: ${tradeName}`;

      const { data, error } = await supabase.functions.invoke("project-assistant", {
        body: {
          question: q,
          contextDocument: contextWithSow,
          conversationHistory: aiConvHistory,
        },
      });

      if (error) {
        let detail = error.message;
        try { const b = await (error as any).context?.json?.(); detail = b?.error || b?.message || detail; } catch {}
        throw new Error(detail);
      }
      if (data?.error) throw new Error(data.error);

      const answer: string = data?.answer || "No response returned.";
      setAiMessages(prev => [...prev, { role: "assistant", content: answer }]);
      setAiConvHistory(prev => [
        ...prev,
        { role: "user", content: q },
        { role: "assistant", content: answer },
      ].slice(-20));
    } catch (e: any) {
      setAiMessages(prev => [...prev, { role: "assistant", content: `Error: ${e.message}`, error: true }]);
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiToCurrentSection = (content: string) => {
    const lines = content.split("\n").map(l => l.replace(/^[-•*]\s*/, "").trim()).filter(l => l.length > 0 && !l.match(/^#+/));
    const sectionKey = getSectionKey(activeSection);
    if (!sectionKey) return;
    const currentItems = (state as any)[sectionKey];
    if (Array.isArray(currentItems)) {
      upd(sectionKey as any, [...currentItems, ...lines.filter(l => !currentItems.includes(l))]);
      showToastMsg(`Applied ${lines.length} item(s) to ${SECTION_LABELS[activeSection]}`);
    }
  };

  const getSectionKey = (idx: number): keyof EditorState | null => {
    const keys: (keyof EditorState | null)[] = [
      "scopeItems", "miscItems", "documentList", "submittals", "exclusions",
      "referenceSpecs", "schedule", "allowances", "alternates", "unitPrices",
      "laborRates", "recapitulation",
    ];
    return keys[idx] ?? null;
  };

  const handleExportSow = async () => {
    showToastMsg("Generating SOW...");
    try {
      await generateSowDocx({
        projectName,
        address,
        date: new Date().toLocaleDateString(),
        tradeName,
        tradeContact,
        divisionName,
        sections: state,
      });
    } catch (e: any) {
      showToastMsg("Export failed: " + e.message);
    }
  };

  const handleExportBidForm = async () => {
    showToastMsg("Generating Bid Form...");
    const tmpl = SOW_TEMPLATES[divisionId];
    try {
      await generateBidFormDocx({
        projectName,
        address,
        dueDate: "",
        divisionName,
        tradeName,
        scopeItems: state.scopeItems,
        alternates: state.alternates,
        unitPriceItems: state.unitPrices,
        laborCategories: tmpl?.laborCategories || [],
        proposals,
      });
    } catch (e: any) {
      showToastMsg("Export failed: " + e.message);
    }
  };

  // ─── Section content renderers ────
  const renderSectionContent = () => {
    switch (activeSection) {
      case 0: // Scope of Work
        return <ListEditor items={state.scopeItems} onChange={v => upd("scopeItems", v)} placeholder="Furnish and install..." T={T} />;
      case 1: // Misc. Requirements
        return <ListEditor items={state.miscItems} onChange={v => upd("miscItems", v)} placeholder="Misc. requirement..." T={T} />;
      case 2: // Document List
        return (
          <textarea
            value={state.documentList}
            onChange={e => upd("documentList", e.target.value)}
            rows={8}
            style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "8px 12px", resize: "vertical" as any, lineHeight: 1.6, boxSizing: "border-box" as any, outline: "none" }}
            placeholder="List contract documents, drawing sets, and specifications that govern this scope..."
          />
        );
      case 3: // Submittals
        return (
          <div style={{ display: "flex", flexDirection: "column" as any, gap: 10 }}>
            <div style={{
              fontSize: 10, color: T.textMuted, padding: "7px 12px",
              background: T.bgApp, border: `1px solid ${T.borderSecondary}`,
              borderRadius: 6, lineHeight: 1.5,
            }}>
              {submittalsForDivision && submittalsForDivision.length > 0
                ? `${submittalsForDivision.length} submittal${submittalsForDivision.length !== 1 ? "s" : ""} pulled from the Submittals tab. Add, edit, or remove as needed.`
                : "No submittals have been added for this division in the Submittals tab yet — showing template defaults. Populate the Submittals tab to sync live data here."}
            </div>
            <ListEditor items={state.submittals} onChange={v => upd("submittals", v)} placeholder="Submittal item..." T={T} />
          </div>
        );
      case 4: // Exclusions
        return <ListEditor items={state.exclusions} onChange={v => upd("exclusions", v)} placeholder="Excluded work item..." T={T} />;
      case 5: // Reference Standards
        return <ListEditor items={state.referenceSpecs} onChange={v => upd("referenceSpecs", v)} placeholder="ASTM / NRCA / AISC standard..." T={T} />;
      case 6: { // Schedule
        const derived = scheduleData ? deriveScheduleForDivision(divisionId, scheduleData) : null;
        const fmtDisplay = (iso: string) => {
          if (!iso) return "";
          const [y, m, d] = iso.split("-");
          return `${parseInt(m)}/${parseInt(d)}/${y}`;
        };
        const alreadyApplied = derived &&
          state.schedule.start === derived.start &&
          state.schedule.end === derived.end;
        return (
          <div style={{ display: "flex", flexDirection: "column" as any, gap: 14 }}>

            {/* Pull-from-schedule banner */}
            {derived ? (
              <div style={{
                display: "flex", alignItems: "center", gap: 12,
                background: alreadyApplied ? `${T.borderAccent}0e` : `${T.borderAccent}15`,
                border: `1px solid ${alreadyApplied ? T.borderAccentFaint : T.borderAccent}55`,
                borderRadius: 6, padding: "10px 14px",
              }}>
                <div style={{ opacity: 0.7 }}><Icon.Clock size={16} /></div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.borderAccent, marginBottom: 2 }}>
                    {alreadyApplied ? "Schedule pulled" : "Schedule data available"}
                  </div>
                  <div style={{ fontSize: 10, color: T.textMuted, lineHeight: 1.5 }}>
                    {derived.phaseNames.join(" · ")}
                    {" — "}
                    <span style={{ color: T.textTertiary }}>
                      {fmtDisplay(derived.start)} → {fmtDisplay(derived.end)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (!alreadyApplied) {
                      upd("schedule", { ...state.schedule, start: derived.start, end: derived.end });
                    }
                  }}
                  disabled={!!alreadyApplied}
                  style={{
                    flexShrink: 0, padding: "6px 14px", borderRadius: 6,
                    background: alreadyApplied ? "transparent" : T.borderAccent,
                    color: alreadyApplied ? T.textMuted : T.textOnGold,
                    fontSize: 11, fontWeight: 700, fontFamily: FONT,
                    cursor: alreadyApplied ? "default" : "pointer",
                    border: alreadyApplied ? `1px solid ${T.borderSecondary}` : "none",
                  } as any}
                >
                  {alreadyApplied ? "✓ Applied" : "Pull from Schedule"}
                </button>
              </div>
            ) : scheduleData && !derived ? (
              <div style={{
                fontSize: 11, color: T.textMuted, padding: "9px 14px",
                background: T.bgApp, border: `1px dashed ${T.borderSecondary}`,
                borderRadius: 6,
              }}>
                No phases in the Schedule module are currently linked to {divisionName}. Link this division to a phase in the Schedule tab to auto-populate dates here.
              </div>
            ) : null}

            <div style={{ display: "flex", gap: 12 }}>
              <label style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase" as any, letterSpacing: ".07em", marginBottom: 5 }}>Start Date</div>
                <input type="date" value={state.schedule.start}
                  onChange={e => upd("schedule", { ...state.schedule, start: e.target.value })}
                  style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "7px 10px", outline: "none", boxSizing: "border-box" as any }} />
              </label>
              <label style={{ flex: 1 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase" as any, letterSpacing: ".07em", marginBottom: 5 }}>Completion Date</div>
                <input type="date" value={state.schedule.end}
                  onChange={e => upd("schedule", { ...state.schedule, end: e.target.value })}
                  style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "7px 10px", outline: "none", boxSizing: "border-box" as any }} />
              </label>
              <label style={{ width: 100 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase" as any, letterSpacing: ".07em", marginBottom: 5 }}>Mobilizations</div>
                <input type="number" min={1} value={state.mobilizations}
                  onChange={e => upd("mobilizations", parseInt(e.target.value) || 1)}
                  style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "7px 10px", outline: "none", boxSizing: "border-box" as any }} />
              </label>
            </div>
            <label>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, textTransform: "uppercase" as any, letterSpacing: ".07em", marginBottom: 5 }}>Schedule Notes</div>
              <textarea
                value={state.schedule.notes}
                onChange={e => upd("schedule", { ...state.schedule, notes: e.target.value })}
                rows={4}
                style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, fontSize: 12, padding: "8px 12px", resize: "vertical" as any, lineHeight: 1.6, boxSizing: "border-box" as any, outline: "none" }}
                placeholder="Long-lead items, phasing notes, seasonal constraints..."
              />
            </label>
          </div>
        );
      }
      case 7: // Allowances
        return (
          <div style={{ display: "flex", flexDirection: "column" as any, gap: 10 }}>
            <div style={{
              fontSize: 10, color: T.textMuted, padding: "7px 12px",
              background: T.bgApp, border: `1px solid ${T.borderSecondary}`,
              borderRadius: 6, lineHeight: 1.5,
            }}>
              {allowancesForDivision && allowancesForDivision.length > 0
                ? `${allowancesForDivision.length} allowance item${allowancesForDivision.length !== 1 ? "s" : ""} pulled from the estimate. Add, edit, or remove as needed.`
                : `No items are flagged as Allowance in the estimate for this division. Mark items with "Allowance" cost status in the Estimate tab to populate this section automatically.`}
            </div>
            <ListEditor items={state.allowances} onChange={v => upd("allowances", v)} placeholder="Allowance item and amount..." T={T} />
          </div>
        );
      case 8: // Alternates
        return (
          <PairListEditor
            items={state.alternates}
            onChange={v => upd("alternates", v)}
            labelA="Alternate Description"
            labelB="Value / Add-Deduct"
            T={T}
          />
        );
      case 9: // Unit Prices
        return <ListEditor items={state.unitPrices} onChange={v => upd("unitPrices", v)} placeholder="Unit price item and unit..." T={T} />;
      case 10: // Labor Rates
        return <ListEditor items={state.laborRates} onChange={v => upd("laborRates", v)} placeholder="Category: $___/hr ST | $___/hr OT" T={T} />;
      case 11: // Recapitulation
        return (
          <div style={{ display: "flex", flexDirection: "column" as any, gap: 8 }}>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
              Pre-populated from project packages. Adjust values as needed.
            </div>
            <PairListEditor
              items={state.recapitulation}
              onChange={v => upd("recapitulation", v)}
              labelA="Package / Description"
              labelB="Amount"
              T={T}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const inputStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    padding: "2px 4px",
    color: T.textPrimary,
    fontFamily: FONT,
    fontSize: 12,
    outline: "none",
    width: "100%",
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 400,
      background: T.bgApp,
      display: "flex", flexDirection: "column" as any,
      fontFamily: FONT,
    }}>
      {/* ── Top bar ── */}
      <div style={{
        display: "flex", alignItems: "center", gap: 10,
        padding: "0 16px",
        height: 52,
        background: T.bgHeader,
        borderBottom: `1px solid ${T.borderPrimary}`,
        flexShrink: 0,
      }}>
        {/* Back button */}
        <button
          onClick={onClose}
          style={{
            display: "flex", alignItems: "center", gap: 5,
            background: "none", border: `1px solid ${T.borderSecondary}`,
            color: T.textTertiary, cursor: "pointer",
            padding: "5px 12px", borderRadius: 6,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, flexShrink: 0,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderAccent; e.currentTarget.style.color = T.borderAccent; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.borderSecondary; e.currentTarget.style.color = T.textTertiary; }}
        >
          ← Back
        </button>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as any }}>
            Scope of Work — <span style={{ color: T.borderAccent }}>{divisionName}</span>
            {tradeName && <span style={{ color: T.textMuted, fontWeight: 400 }}> / {tradeName}</span>}
          </div>
          {projectName && (
            <div style={{ fontSize: 10, color: T.textWeak, marginTop: 1 }}>{projectName}{address ? ` — ${address}` : ""}</div>
          )}
        </div>

        {/* Auto-save status */}
        <div style={{
          fontSize: 10, flexShrink: 0,
          color: saveStatus === "saved" ? T.positive || "#5dba7d" : T.textWeak,
          opacity: saveStatus ? 1 : 0,
          transition: "opacity .3s, color .2s",
          minWidth: 70, textAlign: "right" as any,
        }}>
          {saveStatus === "saving" ? "Saving…" : "✓ Draft saved"}
        </div>

        <button
          onClick={handleExportSow}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            background: T.borderAccent, border: "none",
            color: T.textOnGold, cursor: "pointer",
            padding: "7px 16px", borderRadius: 6,
            fontFamily: FONT, fontSize: 12, fontWeight: 700, flexShrink: 0,
          }}
        >
          <Icon.Download size={13} /> Export SOW (.docx)
        </button>

        <button
          onClick={handleExportBidForm}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            background: "none", border: `1px solid ${T.borderAccent}`,
            color: T.borderAccent, cursor: "pointer",
            padding: "6px 16px", borderRadius: 6,
            fontFamily: FONT, fontSize: 12, fontWeight: 600, flexShrink: 0,
          }}
        >
          <Icon.FileText size={13} /> Export Bid Form (.docx)
        </button>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* ── Left sidebar ── */}
        <div style={{
          width: 220, flexShrink: 0,
          background: T.bgPanel,
          borderRight: `1px solid ${T.borderPrimary}`,
          display: "flex", flexDirection: "column" as any,
          overflowY: "auto" as any,
        }}>
          <div style={{ padding: "10px 12px 4px", fontSize: 10, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" as any }}>
            Sections
          </div>
          {SECTION_LABELS.map((label, idx) => {
            const isActive = activeSection === idx;
            return (
              <button
                key={idx}
                onClick={() => setActiveSection(idx)}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "9px 14px",
                  background: "none", border: "none", cursor: "pointer",
                  textAlign: "left" as any, width: "100%",
                  borderLeft: isActive ? `3px solid ${T.borderAccent}` : "3px solid transparent",
                  backgroundColor: isActive ? `${T.borderAccent}14` : "transparent",
                  fontFamily: FONT,
                }}
              >
                <span style={{
                  fontSize: 10, fontWeight: 700,
                  color: isActive ? T.borderAccent : T.textWeak,
                  minWidth: 18,
                }}>
                  {idx + 1}
                </span>
                <span style={{
                  fontSize: 12,
                  color: isActive ? T.textPrimary : T.textTertiary,
                  fontWeight: isActive ? 600 : 400,
                }}>
                  {label}
                </span>
              </button>
            );
          })}

          {/* AI Enhance button */}
          <div style={{ marginTop: "auto", padding: "12px 12px 16px", borderTop: `1px solid ${T.borderPrimary}` }}>
            <button
              onClick={() => setShowAI(s => !s)}
              style={{
                width: "100%",
                display: "flex", alignItems: "center", gap: 8,
                background: showAI ? `${T.borderAccent}18` : T.bgInput,
                border: `1px solid ${showAI ? T.borderAccent : T.borderSecondary}`,
                borderRadius: 6, cursor: "pointer",
                padding: "8px 12px",
                fontFamily: FONT, fontSize: 12, fontWeight: 600,
                color: showAI ? T.borderAccent : T.textMuted,
              }}
            >
              <Icon.Bot size={14} />
              <span>✦ Ask AI to Enhance</span>
            </button>
            <button
              onClick={handleResetToDefaults}
              style={{
                width: "100%", marginTop: 6,
                background: "none", border: "none",
                color: T.textWeak, cursor: "pointer",
                fontFamily: FONT, fontSize: 10, padding: "4px 0",
                textAlign: "center" as any,
              }}
              onMouseEnter={e => (e.currentTarget.style.color = T.negative || "#d46a6a")}
              onMouseLeave={e => (e.currentTarget.style.color = T.textWeak)}
            >
              Reset to template defaults
            </button>
          </div>
        </div>

        {/* ── Main content ── */}
        <div style={{
          flex: 1, display: "flex", flexDirection: "column" as any,
          overflow: "hidden",
        }}>
          <div style={{ flex: 1, overflowY: "auto" as any, padding: "24px 28px" }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.borderAccent, letterSpacing: ".07em", textTransform: "uppercase" as any, marginBottom: 6 }}>
                {activeSection + 1}. {SECTION_LABELS[activeSection]}
              </div>
            </div>
            {renderSectionContent()}
          </div>
        </div>

        {/* ── AI Panel ── */}
        {showAI && (
          <div style={{
            width: 460, flexShrink: 0,
            background: T.bgPanel,
            borderLeft: `1px solid ${T.borderPrimary}`,
            display: "flex", flexDirection: "column" as any,
          }}>
            {/* AI panel header */}
            <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.borderPrimary}`, display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <Icon.Bot size={14} />
              <span style={{ fontSize: 13, fontWeight: 600, color: T.textPrimary, flex: 1 }}>AI Scope Assistant</span>
              <button onClick={() => setShowAI(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textWeak, padding: 4 }}>
                <Icon.X size={13} />
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: "auto" as any, padding: "12px 14px", display: "flex", flexDirection: "column" as any, gap: 10 }}>
              {aiMessages.map((msg, i) => {
                const isUser = msg.role === "user";
                return (
                  <div key={i} style={{ display: "flex", flexDirection: "column" as any, alignItems: isUser ? "flex-end" : "flex-start", gap: 4 }}>
                    <div style={{
                      maxWidth: "90%",
                      background: isUser ? T.borderAccent : T.bgInput,
                      color: isUser ? T.textOnGold : msg.error ? T.negative : T.textPrimary,
                      borderRadius: isUser ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                      padding: "9px 13px",
                      fontSize: 12,
                      lineHeight: 1.6,
                      border: isUser ? "none" : `1px solid ${T.borderSecondary}`,
                      whiteSpace: "pre-wrap" as any,
                    }}>
                      {msg.content}
                    </div>
                    {!isUser && !msg.error && (
                      <button
                        onClick={() => applyAiToCurrentSection(msg.content)}
                        style={{
                          background: "none", border: `1px solid ${T.borderSecondary}`,
                          borderRadius: 6, cursor: "pointer", padding: "3px 10px",
                          fontFamily: FONT, fontSize: 10, color: T.textMuted,
                          display: "flex", alignItems: "center", gap: 5,
                        }}
                        title={`Apply suggestions to ${SECTION_LABELS[activeSection]}`}
                      >
                        <Icon.Plus size={10} /> Apply to {SECTION_LABELS[activeSection]}
                      </button>
                    )}
                  </div>
                );
              })}
              {aiLoading && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                  <span style={{ fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>AI is thinking...</span>
                </div>
              )}
              <div ref={aiEndRef} />
            </div>

            {/* Input */}
            <div style={{ padding: "10px 12px", borderTop: `1px solid ${T.borderPrimary}`, flexShrink: 0, display: "flex", gap: 8 }}>
              <textarea
                ref={aiInputRef}
                value={aiInput}
                onChange={e => setAiInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAiSend(); }
                }}
                placeholder="Ask for improvements to this scope..."
                rows={2}
                style={{
                  flex: 1, background: T.bgInput, border: `1px solid ${T.borderSecondary}`,
                  borderRadius: 6, color: T.textPrimary, fontFamily: FONT,
                  fontSize: 12, padding: "8px 10px", resize: "none" as any,
                  lineHeight: 1.5, outline: "none",
                }}
              />
              <button
                onClick={handleAiSend}
                disabled={!aiInput.trim() || aiLoading}
                style={{
                  background: T.borderAccent, border: "none", borderRadius: 6,
                  cursor: aiInput.trim() && !aiLoading ? "pointer" : "not-allowed",
                  color: T.textOnGold, padding: "0 14px",
                  opacity: aiInput.trim() && !aiLoading ? 1 : 0.45,
                  flexShrink: 0,
                }}
              >
                <Icon.ArrowUp size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Toast ── */}
      {toast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: T.bgToast, color: T.textPrimary, border: `1px solid ${T.borderAccent}55`,
          borderRadius: 6, padding: "10px 20px", fontSize: 12, fontWeight: 600,
          boxShadow: "0 4px 16px #00000040", zIndex: 500, fontFamily: FONT,
          pointerEvents: "none",
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
